// Catalog — subscription Plans + AI Models on a tabbed page.
// Both surfaces are CRUD over a tiny catalog (<10 rows each), so an
// inline-edit table beats a separate form page. Mutations write through
// PATCH and the server logs an audit row automatically.
//
// PX-149 broadened both tabs to cover the monetization fields PX-143
// added: plans carry entitlement_id / generation_discount_pct /
// watermark_free; models carry allowed_plan_ids (the advanced-model
// gate) and show a client-side credit-cost suggestion derived from the
// linked fal.ai model's price. The per-SKU allowance catalog (products /
// packs / bonuses) lives on the separate Monetization page.

import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { PlanMultiPicker } from '@/components/PlanPicker'
import {
  Checkbox,
  DeleteButton,
  ErrorBanner,
  Field,
  FormActions,
  InlineNumberCell,
  InlineTextCell,
  NewItemButton,
  TabSwitcher,
  Toggle,
  firstApiError,
} from '@/components/FormControls'
import {
  PERM,
  type AdminAppVersionConfigResponse,
  type AdminAppVersionConfigUpdate,
  type AdminDailyRewardConfigResponse,
  type AdminDailyRewardConfigUpdate,
  type AdminModelCreate,
  type AdminModelResponse,
  type AdminModelUpdate,
  type ModelInputKind,
  type AdminPlanCreate,
  type AdminPlanResponse,
  type AdminPlanUpdate,
  type AdminRatingConfigResponse,
  type AdminRatingConfigUpdate,
  type FalModelDetail,
  type RatingMode,
} from '@/types/api'

type Tab = 'plans' | 'models' | 'rating' | 'daily-reward' | 'app-version'

export function CatalogPage() {
  const { profile } = useAuth()
  // PX-73: tab visibility tracks the permission required to load the
  // corresponding catalog. Roles without `plans.read` get only the
  // Models tab; without `models.read` only Plans. A role with neither
  // never lands on this page (RequirePermission blocks it upstream).
  const canSeePlans = hasPerm(profile, PERM.plans_read)
  const canSeeModels = hasPerm(profile, PERM.models_read)
  const [tab, setTab] = useState<Tab>(canSeePlans ? 'plans' : 'models')

  // Defensive — if profile resolves later (e.g. silent refresh boot), make
  // sure a curator who saw `plans` flash from initial state gets bumped.
  useEffect(() => {
    if (!canSeePlans && tab === 'plans') setTab('models')
  }, [canSeePlans, tab])

  const tabDefs: { id: Tab; label: string }[] = [
    ...(canSeePlans ? [{ id: 'plans' as Tab, label: 'Plans' }] : []),
    ...(canSeeModels ? [{ id: 'models' as Tab, label: 'Models' }] : []),
    // Rating + daily-reward master-switches live under the same `plans.*`
    // permission family and `/admin/catalog/` API prefix.
    ...(canSeePlans ? [{ id: 'rating' as Tab, label: 'Rating' }] : []),
    ...(canSeePlans ? [{ id: 'daily-reward' as Tab, label: 'Daily Reward' }] : []),
    ...(canSeePlans ? [{ id: 'app-version' as Tab, label: 'App Version' }] : []),
  ]

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Catalog</h1>
        <p className="text-sm text-ink-400">
          {canSeePlans ? 'Subscription plans and AI models.' : 'AI models.'}
        </p>
      </header>

      {tabDefs.length > 1 && (
        <TabSwitcher tabs={tabDefs} current={tab} onChange={setTab} />
      )}

      {tab === 'plans' ? (
        <PlansTab />
      ) : tab === 'models' ? (
        <ModelsTab />
      ) : tab === 'rating' ? (
        <RatingTab />
      ) : tab === 'daily-reward' ? (
        <DailyRewardTab />
      ) : (
        <AppVersionTab />
      )}
    </div>
  )
}

// ----- Plans ---------------------------------------------------------

function PlansTab() {
  const queryClient = useQueryClient()
  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
  })

  const patchPlan = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AdminPlanUpdate }) =>
      request<AdminPlanResponse>(`/admin/catalog/plans/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'plans'] }),
  })

  const createPlan = useMutation({
    mutationFn: (body: AdminPlanCreate) =>
      request<AdminPlanResponse>('/admin/catalog/plans', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'plans'] }),
  })

  const deletePlan = useMutation({
    mutationFn: (id: number) =>
      request(`/admin/catalog/plans/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'plans'] }),
  })

  const [creating, setCreating] = useState(false)
  const formError = firstApiError(createPlan.error)
  const rowError = firstApiError(patchPlan.error, deletePlan.error)

  return (
    <div>
      {rowError && <ErrorBanner error={rowError} />}

      <NewItemButton label="New plan" onClick={() => setCreating(true)} />

      {creating && (
        <PlanForm
          error={formError}
          onSubmit={(body) => {
            createPlan.mutate(body, { onSuccess: () => setCreating(false) })
          }}
          onCancel={() => { createPlan.reset(); setCreating(false) }}
          submitting={createPlan.isPending}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Rank</th>
              <th className="px-4 py-3">Entitlement</th>
              <th className="px-4 py-3" title="Per-generation token discount %">Disc %</th>
              <th className="px-4 py-3" title="No watermark on results">No WM</th>
              <th className="px-4 py-3" title="Token usage analytics unlocked on the Subscription tab">Usage</th>
              <th className="px-4 py-3" title="Unlimited generations (skip per-gen debit)">Unlim</th>
              <th className="px-4 py-3" title="Max AI profiles a user on this plan may keep (PX-416)">AI prof</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {plansQuery.isLoading && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {plansQuery.data?.map((p) => (
              <tr key={p.id} className="border-b border-ink-100 last:border-0">
                <td className="px-4 py-3 font-mono text-xs">{p.slug}</td>
                <td className="px-4 py-3 font-medium text-ink-900">
                  <InlineTextCell
                    value={p.name}
                    disabled={patchPlan.isPending}
                    onCommit={(name) => patchPlan.mutate({ id: p.id, body: { name } })}
                  />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={p.rank}
                    min={0}
                    disabled={patchPlan.isPending}
                    onCommit={(rank) => patchPlan.mutate({ id: p.id, body: { rank } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <InlineTextCell
                    value={p.entitlement_id ?? ''}
                    allowEmpty
                    mono
                    placeholder="—"
                    disabled={patchPlan.isPending}
                    onCommit={(v) =>
                      patchPlan.mutate({ id: p.id, body: { entitlement_id: v === '' ? null : v } })
                    }
                  />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={p.generation_discount_pct}
                    min={0}
                    max={100}
                    widthClass="w-14"
                    disabled={patchPlan.isPending}
                    onCommit={(v) => patchPlan.mutate({ id: p.id, body: { generation_discount_pct: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.watermark_free}
                    disabled={patchPlan.isPending}
                    onChange={(v) => patchPlan.mutate({ id: p.id, body: { watermark_free: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.usage_analytics}
                    disabled={patchPlan.isPending}
                    onChange={(v) => patchPlan.mutate({ id: p.id, body: { usage_analytics: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.unlimited_generations}
                    disabled={patchPlan.isPending}
                    onChange={(v) => patchPlan.mutate({ id: p.id, body: { unlimited_generations: v } })}
                  />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={p.max_ai_profiles}
                    min={1}
                    max={100}
                    widthClass="w-14"
                    disabled={patchPlan.isPending}
                    onCommit={(v) => patchPlan.mutate({ id: p.id, body: { max_ai_profiles: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.is_active}
                    disabled={patchPlan.isPending}
                    onChange={(v) => patchPlan.mutate({ id: p.id, body: { is_active: v } })}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <DeleteButton
                    disabled={deletePlan.isPending}
                    confirmText={`Delete plan "${p.name}"? Refused server-side if anyone subscribes to it.`}
                    onConfirm={() => deletePlan.mutate(p.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PlanForm({
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  onSubmit: (body: AdminPlanCreate) => void
  onCancel: () => void
  submitting: boolean
  error: APIError | null
}) {
  const [body, setBody] = useState<AdminPlanCreate>({
    id: 0,
    slug: '',
    name: '',
    description: '',
    rank: 0,
    revenuecat_product_id: null,
    entitlement_id: null,
    generation_discount_pct: 0,
    watermark_free: false,
    usage_analytics: false,
    unlimited_generations: false,
    max_ai_profiles: 1,
    is_active: true,
    sort_order: 0,
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(body)
      }}
      className="mb-4 rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
    >
      {error && <ErrorBanner error={error} />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Id (SMALLINT, assigned)" type="number" value={String(body.id)} onChange={(v) => setBody({ ...body, id: Number(v) || 0 })} mono />
        <Field label="Slug" value={body.slug} onChange={(slug) => setBody({ ...body, slug })} mono />
        <Field label="Name" value={body.name} onChange={(name) => setBody({ ...body, name })} />
        <Field label="Rank (int)" type="number" value={String(body.rank)} onChange={(v) => setBody({ ...body, rank: Number(v) || 0 })} mono />
        <Field
          label="RC product id"
          value={body.revenuecat_product_id ?? ''}
          onChange={(v) => setBody({ ...body, revenuecat_product_id: v || null })}
          mono
        />
        <Field
          label="Entitlement id (NULL for free)"
          value={body.entitlement_id ?? ''}
          onChange={(v) => setBody({ ...body, entitlement_id: v || null })}
          mono
        />
        <Field
          label="Discount % (0–100)"
          type="number"
          value={String(body.generation_discount_pct ?? 0)}
          onChange={(v) => setBody({ ...body, generation_discount_pct: Number(v) || 0 })}
          mono
        />
        <Field
          label="Max AI profiles"
          type="number"
          value={String(body.max_ai_profiles ?? 1)}
          onChange={(v) => setBody({ ...body, max_ai_profiles: Number(v) || 1 })}
          mono
        />
        <Field
          label="Sort order"
          type="number"
          value={String(body.sort_order ?? 0)}
          onChange={(v) => setBody({ ...body, sort_order: Number(v) || 0 })}
          mono
        />
        <div className="col-span-2 flex flex-wrap items-end gap-4 text-sm">
          <Checkbox
            label="Watermark-free"
            checked={Boolean(body.watermark_free)}
            onChange={(v) => setBody({ ...body, watermark_free: v })}
          />
          <Checkbox
            label="Usage analytics"
            checked={Boolean(body.usage_analytics)}
            onChange={(v) => setBody({ ...body, usage_analytics: v })}
          />
          <Checkbox
            label="Unlimited"
            checked={Boolean(body.unlimited_generations)}
            onChange={(v) => setBody({ ...body, unlimited_generations: v })}
          />
          <Checkbox
            label="Active"
            checked={Boolean(body.is_active)}
            onChange={(v) => setBody({ ...body, is_active: v })}
          />
        </div>
      </div>
      <FormActions onCancel={onCancel} submitting={submitting} />
    </form>
  )
}

// ----- Models --------------------------------------------------------

// PX-477: pipeline shape. `output_kind` is DERIVED from `input_kind`
// (image_edit → image, all others → video) and is read-only in the UI —
// we compute it client-side only for display, never send it.
const INPUT_KIND_OPTIONS: { id: ModelInputKind; label: string }[] = [
  { id: 'image_edit', label: 'Image edit' },
  { id: 'image_to_video', label: 'Image → video' },
  { id: 'video_to_video', label: 'Video → video' },
  { id: 'text_to_video', label: 'Text → video' },
]

function deriveOutputKind(input: ModelInputKind): 'image' | 'video' {
  return input === 'image_edit' ? 'image' : 'video'
}

function ModelsTab() {
  const queryClient = useQueryClient()
  const modelsQuery = useQuery({
    queryKey: ['admin', 'catalog', 'models'],
    queryFn: () => request<AdminModelResponse[]>('/admin/catalog/models'),
  })
  // Plans power the allowed_plan_ids multi-picker + tag rendering. Shared
  // cache key with PlansTab so switching tabs doesn't refetch.
  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
  })
  const plans = plansQuery.data ?? []

  const patchModel = useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdminModelUpdate }) =>
      request<AdminModelResponse>(`/admin/catalog/models/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'models'] }),
  })

  const createModel = useMutation({
    mutationFn: (body: AdminModelCreate) =>
      request<AdminModelResponse>('/admin/catalog/models', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'models'] }),
  })

  const deleteModel = useMutation({
    mutationFn: (id: string) =>
      request(`/admin/catalog/models/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'models'] }),
  })

  const [creating, setCreating] = useState(false)
  const formError = firstApiError(createModel.error)
  const rowError = firstApiError(patchModel.error, deleteModel.error)

  return (
    <div>
      {rowError && <ErrorBanner error={rowError} />}

      <NewItemButton label="New model" onClick={() => setCreating(true)} />

      {creating && (
        <ModelForm
          plans={plans}
          error={formError}
          onSubmit={(body) => createModel.mutate(body, { onSuccess: () => setCreating(false) })}
          onCancel={() => { createModel.reset(); setCreating(false) }}
          submitting={createModel.isPending}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Model</th>
              <th className="px-4 py-3" title="User-friendly label shown in the iOS app (falls back to Name when empty)">Display name</th>
              <th className="px-4 py-3" title="Input pipeline → derived output kind (PX-477)">Pipeline</th>
              <th className="px-4 py-3">fal model id</th>
              <th className="px-4 py-3">Credits</th>
              <th className="px-4 py-3" title="Suggested = round(fal price × 500)">Suggested</th>
              <th className="px-4 py-3">Allowed plans</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {modelsQuery.isLoading && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {modelsQuery.data?.map((m) => (
              <tr key={m.id} className="border-b border-ink-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-ink-900">{m.name}</div>
                  <div className="font-mono text-xs text-ink-400">{m.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <InlineTextCell
                    value={m.display_name ?? ''}
                    allowEmpty
                    placeholder="—"
                    disabled={patchModel.isPending}
                    onCommit={(v) => patchModel.mutate({ id: m.id, body: { display_name: v === '' ? null : v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-700">
                      {m.input_kind.replace(/_/g, ' ')}
                    </span>
                    {m.output_kind === 'video' && (
                      <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand-600">
                        video
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{m.fal_model_id}</td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={m.credit_cost}
                    min={0}
                    disabled={patchModel.isPending}
                    onCommit={(v) => patchModel.mutate({ id: m.id, body: { credit_cost: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <SuggestedCreditCost
                    falModelId={m.fal_model_id}
                    currentCost={m.credit_cost}
                    disabled={patchModel.isPending}
                    onApply={(v) => patchModel.mutate({ id: m.id, body: { credit_cost: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <PlanMultiPicker
                    value={m.allowed_plan_ids}
                    plans={plans}
                    disabled={patchModel.isPending || plansQuery.isLoading}
                    onChange={(ids) => patchModel.mutate({ id: m.id, body: { allowed_plan_ids: ids } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={m.is_active}
                    disabled={patchModel.isPending}
                    onChange={(v) => patchModel.mutate({ id: m.id, body: { is_active: v } })}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <DeleteButton
                    disabled={deleteModel.isPending}
                    confirmText={`Delete model "${m.name}"? Refused server-side if any photoshoot uses it.`}
                    onConfirm={() => deleteModel.mutate(m.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Suggested credit cost — the backend doesn't store this, so we derive it
// client-side from the linked fal.ai model's headline price using the
// EPIC-1 token formula: 1 token = 1¢, ×5 markup → round(price_usd × 500).
// e.g. nano-banana-2/edit at $0.08 → 40 tokens. Needs fal_models.read;
// degrades silently to "—" when the row has no parseable price or the
// operator lacks that permission.
function SuggestedCreditCost({
  falModelId,
  currentCost,
  onApply,
  disabled,
}: {
  falModelId: string
  currentCost: number
  onApply: (v: number) => void
  disabled?: boolean
}) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'fal-models', 'detail', falModelId],
    queryFn: () => request<FalModelDetail>(`/admin/fal-models/${falModelId}`),
    enabled: !!falModelId,
    staleTime: 5 * 60_000,
    retry: false,  // a 403 (no fal_models.read) / 404 shouldn't thrash
  })

  const priceStr = detailQuery.data?.pricing_amount_usd
  const price = priceStr != null ? Number(priceStr) : NaN
  if (!Number.isFinite(price) || price <= 0) {
    return <span className="text-xs text-ink-300">—</span>
  }
  const suggested = Math.round(price * 500)
  const matches = suggested === currentCost
  return (
    <span className="inline-flex items-center gap-2 text-xs tabular-nums">
      <span className={matches ? 'text-emerald-600' : 'text-ink-500'}>≈ {suggested}</span>
      {!matches && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onApply(suggested)}
          className="rounded border border-ink-200 px-1.5 py-0.5 font-medium text-brand-600 transition hover:border-brand-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          use
        </button>
      )}
    </span>
  )
}

function ModelForm({
  onSubmit,
  onCancel,
  submitting,
  plans,
  error,
}: {
  onSubmit: (body: AdminModelCreate) => void
  onCancel: () => void
  submitting: boolean
  plans: AdminPlanResponse[]
  error: APIError | null
}) {
  const [body, setBody] = useState<AdminModelCreate>({
    slug: '',
    name: '',
    display_name: null,
    fal_model_id: '',
    credit_cost: 5,
    allowed_plan_ids: [],
    description: '',
    input_kind: 'image_edit',
    is_active: true,
    sort_order: 0,
  })

  // PX-477: `fal_extra_params` is a free-form JSON object. We keep it as raw
  // text and parse on submit — invalid JSON blocks the create with an inline
  // error. Empty → send null.
  const [extraParamsText, setExtraParamsText] = useState('')
  const [extraParamsError, setExtraParamsError] = useState<string | null>(null)

  const inputKind = body.input_kind ?? 'image_edit'
  const outputKind = deriveOutputKind(inputKind)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    let falExtraParams: Record<string, unknown> | null = null
    const trimmed = extraParamsText.trim()
    if (trimmed !== '') {
      let parsed: unknown
      try {
        parsed = JSON.parse(trimmed)
      } catch {
        setExtraParamsError('Invalid JSON.')
        return
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setExtraParamsError('Must be a JSON object (e.g. {"character_orientation": "video"}).')
        return
      }
      falExtraParams = parsed as Record<string, unknown>
    }
    setExtraParamsError(null)
    // `output_kind` is derived server-side — never send it.
    onSubmit({ ...body, fal_extra_params: falExtraParams })
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-4 rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
    >
      {error && <ErrorBanner error={error} />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug" value={body.slug} onChange={(v) => setBody({ ...body, slug: v })} mono />
        <Field label="Name" value={body.name} onChange={(v) => setBody({ ...body, name: v })} />
        <Field label="Display name (optional, shown in app)" value={body.display_name ?? ''} onChange={(v) => setBody({ ...body, display_name: v || null })} />
        <Field label="fal model id" value={body.fal_model_id} onChange={(v) => setBody({ ...body, fal_model_id: v })} mono />
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-ink-500">
            Input kind
            <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-700">
              output: {outputKind}
            </span>
          </span>
          <select
            value={inputKind}
            onChange={(e) => setBody({ ...body, input_kind: e.target.value as ModelInputKind })}
            className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          >
            {INPUT_KIND_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
        <Field label="Credit cost" type="number" value={String(body.credit_cost)} onChange={(v) => setBody({ ...body, credit_cost: Number(v) || 0 })} mono />
        <Field label="Sort order" type="number" value={String(body.sort_order ?? 0)} onChange={(v) => setBody({ ...body, sort_order: Number(v) || 0 })} mono />
        <Checkbox label="Active" checked={Boolean(body.is_active)} onChange={(v) => setBody({ ...body, is_active: v })} />
        <label className="col-span-2 block text-sm">
          <span className="mb-1 block text-xs font-medium text-ink-500">
            fal extra params (JSON object, optional — merged into the fal payload)
          </span>
          <textarea
            value={extraParamsText}
            onChange={(e) => { setExtraParamsText(e.target.value); setExtraParamsError(null) }}
            rows={2}
            placeholder='{"character_orientation": "video"}'
            className="w-full rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {extraParamsError && (
            <span className="mt-1 block text-xs text-red-600">{extraParamsError}</span>
          )}
        </label>
        <div className="col-span-2">
          <span className="mb-1 block text-xs font-medium text-ink-500">Allowed plans (empty = all)</span>
          <PlanMultiPicker
            value={body.allowed_plan_ids ?? []}
            plans={plans}
            onChange={(ids) => setBody({ ...body, allowed_plan_ids: ids })}
          />
        </div>
      </div>
      <FormActions onCancel={onCancel} submitting={submitting} />
    </form>
  )
}

// ----- Rating master-switch ------------------------------------------

function RatingTab() {
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, PERM.plans_write)
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['admin', 'catalog', 'rating-config'],
    queryFn: () => request<AdminRatingConfigResponse>('/admin/catalog/rating-config'),
  })

  const patchConfig = useMutation({
    mutationFn: (body: AdminRatingConfigUpdate) =>
      request<AdminRatingConfigResponse>('/admin/catalog/rating-config', { method: 'PATCH', body }),
    onSuccess: (data) =>
      queryClient.setQueryData(['admin', 'catalog', 'rating-config'], data),
  })

  const server = configQuery.data
  const [mode, setMode] = useState<RatingMode>('off')
  const [bonus, setBonus] = useState('0')

  // Seed the draft from the server value once it loads (and re-seed after a
  // save, since `setQueryData` swaps `server`). react-query's structural
  // sharing keeps the reference stable when a background refetch returns an
  // unchanged value, so it won't clobber an in-progress edit.
  useEffect(() => {
    if (server) {
      setMode(server.mode as RatingMode)
      setBonus(String(server.bonus_credits))
    }
  }, [server])

  const error = firstApiError(configQuery.error, patchConfig.error)
  const bonusNum = Number(bonus)
  const validBonus = Number.isInteger(bonusNum) && bonusNum >= 0
  const dirty =
    server != null && (mode !== server.mode || bonusNum !== server.bonus_credits)

  return (
    <div className="max-w-xl">
      {error && <ErrorBanner error={error} />}

      <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-ink-900">In-app rating</h2>
        <p className="mt-1 text-xs text-ink-400">
          Master switch for the in-app rating surfaces (Profile card +
          return-to-main auto-prompt). Takes effect without an app release.
        </p>

        {configQuery.isLoading ? (
          <p className="mt-4 text-sm text-ink-400">Loading…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-ink-500">Mode</span>
              <select
                value={mode}
                disabled={!canWrite}
                onChange={(e) => setMode(e.target.value as RatingMode)}
                className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:opacity-60"
              >
                <option value="off">off — feature dark everywhere</option>
                <option value="apple">apple — native App Store prompt only</option>
                <option value="custom">custom — our sentiment sheet + token bonus</option>
              </select>
            </label>

            <Field
              label="Bonus tokens (one-time, custom mode only; 0 = no reward)"
              type="number"
              value={bonus}
              onChange={setBonus}
              mono
            />

            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-ink-400">
                {server ? `Updated ${new Date(server.updated_at).toLocaleString()}` : ''}
              </span>
              <button
                type="button"
                disabled={!canWrite || !dirty || !validBonus || patchConfig.isPending}
                onClick={() => patchConfig.mutate({ mode, bonus_credits: bonusNum })}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {patchConfig.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>

            {!canWrite && (
              <p className="text-xs text-ink-400">
                Read-only — editing needs the <code>plans.write</code> permission.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ----- Daily reward master-switch -------------------------------------

const DAILY_REWARD_TRACK_LENGTH = 7

function DailyRewardTab() {
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, PERM.plans_write)
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['admin', 'catalog', 'daily-reward-config'],
    queryFn: () => request<AdminDailyRewardConfigResponse>('/admin/catalog/daily-reward-config'),
  })

  const patchConfig = useMutation({
    mutationFn: (body: AdminDailyRewardConfigUpdate) =>
      request<AdminDailyRewardConfigResponse>('/admin/catalog/daily-reward-config', {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) =>
      queryClient.setQueryData(['admin', 'catalog', 'daily-reward-config'], data),
  })

  const server = configQuery.data
  const [enabled, setEnabled] = useState(false)
  const [track, setTrack] = useState<string[]>(
    Array(DAILY_REWARD_TRACK_LENGTH).fill('0'),
  )

  // Seed the draft from the server value once it loads (and re-seed after a
  // save, since `setQueryData` swaps `server`) — same pattern as RatingTab.
  useEffect(() => {
    if (server) {
      setEnabled(server.enabled)
      setTrack(server.track.map(String))
    }
  }, [server])

  const error = firstApiError(configQuery.error, patchConfig.error)
  const trackNums = track.map(Number)
  const validTrack =
    trackNums.length === DAILY_REWARD_TRACK_LENGTH &&
    trackNums.every((n) => Number.isInteger(n) && n >= 0)
  const dirty =
    server != null &&
    (enabled !== server.enabled ||
      trackNums.some((n, i) => n !== server.track[i]))

  return (
    <div className="max-w-xl">
      {error && <ErrorBanner error={error} />}

      <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-ink-900">Daily login reward</h2>
        <p className="mt-1 text-xs text-ink-400">
          Master switch + per-day token amounts for the 7-day login streak
          (sheet + flame chip in the iOS header). Day 7 cycles back to day 1.
          Takes effect without an app release.
        </p>

        {configQuery.isLoading ? (
          <p className="mt-4 text-sm text-ink-400">Loading…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <label className="flex items-center justify-between text-sm">
              <span className="text-xs font-medium text-ink-500">
                Enabled (off = feature dark everywhere, claim endpoint 403s)
              </span>
              <Toggle checked={enabled} onChange={setEnabled} disabled={!canWrite} />
            </label>

            <div>
              <span className="mb-1 block text-xs font-medium text-ink-500">
                Tokens per day (streak day 1 → 7)
              </span>
              <div className="grid grid-cols-7 gap-2">
                {track.map((value, i) => (
                  <label key={i} className="block text-center">
                    <span className="mb-0.5 block text-[10px] text-ink-400">{i + 1}</span>
                    <input
                      type="number"
                      min={0}
                      value={value}
                      disabled={!canWrite}
                      onChange={(e) =>
                        setTrack((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                      }
                      className="w-full rounded-md border border-ink-200 px-1 py-1.5 text-center font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:opacity-60"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-ink-400">
                {server ? `Updated ${new Date(server.updated_at).toLocaleString()}` : ''}
              </span>
              <button
                type="button"
                disabled={!canWrite || !dirty || !validTrack || patchConfig.isPending}
                onClick={() => patchConfig.mutate({ enabled, track: trackNums })}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {patchConfig.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>

            {!canWrite && (
              <p className="text-xs text-ink-400">
                Read-only — editing needs the <code>plans.write</code> permission.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ----- App version gate (min-version, PX-390) ------------------------

function AppVersionTab() {
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, PERM.plans_write)
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['admin', 'catalog', 'app-version-config'],
    queryFn: () => request<AdminAppVersionConfigResponse>('/admin/catalog/app-version-config'),
  })

  const patchConfig = useMutation({
    mutationFn: (body: AdminAppVersionConfigUpdate) =>
      request<AdminAppVersionConfigResponse>('/admin/catalog/app-version-config', {
        method: 'PATCH',
        body,
      }),
    onSuccess: (data) =>
      queryClient.setQueryData(['admin', 'catalog', 'app-version-config'], data),
  })

  const server = configQuery.data
  const [minBuild, setMinBuild] = useState('0')
  const [latestBuild, setLatestBuild] = useState('0')
  const [latestVersion, setLatestVersion] = useState('')
  const [message, setMessage] = useState('')
  const [storeUrl, setStoreUrl] = useState('')

  // Seed the draft from the server value once it loads (and re-seed after a
  // save). Mirrors RatingTab — react-query structural sharing keeps the
  // reference stable so a background refetch won't clobber an in-progress edit.
  useEffect(() => {
    if (server) {
      setMinBuild(String(server.min_supported_build))
      setLatestBuild(String(server.latest_build))
      setLatestVersion(server.latest_version)
      setMessage(server.update_message)
      setStoreUrl(server.store_url)
    }
  }, [server])

  const error = firstApiError(configQuery.error, patchConfig.error)
  const minNum = Number(minBuild)
  const latestNum = Number(latestBuild)
  const validMin = Number.isInteger(minNum) && minNum >= 0
  const validLatest = Number.isInteger(latestNum) && latestNum >= 0
  // Mirror the backend CHECK + the 422 cross-field guard so Save can't fire a
  // payload the API would reject.
  const validBounds = validMin && validLatest && minNum <= latestNum
  const validVersion = latestVersion.trim().length > 0 && latestVersion.length <= 20
  const valid =
    validBounds && validVersion && storeUrl.length <= 512 && message.length <= 2000
  const dirty =
    server != null &&
    (minNum !== server.min_supported_build ||
      latestNum !== server.latest_build ||
      latestVersion !== server.latest_version ||
      message !== server.update_message ||
      storeUrl !== server.store_url)

  return (
    <div className="max-w-xl">
      {error && <ErrorBanner error={error} />}

      <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-ink-900">App version gate</h2>
        <p className="mt-1 text-xs text-ink-400">
          Min-supported / latest iOS build for the in-app update gate. Compared by
          BUILD NUMBER (CFBundleVersion), not marketing version. Leave both at 0 to
          keep the gate dark. Takes effect without an app release.
        </p>

        {configQuery.isLoading ? (
          <p className="mt-4 text-sm text-ink-400">Loading…</p>
        ) : (
          <div className="mt-4 space-y-4">
            <Field
              label="Min supported build (hard block below this)"
              type="number"
              value={minBuild}
              onChange={setMinBuild}
              mono
            />
            <Field
              label="Latest build (soft “update available” below this)"
              type="number"
              value={latestBuild}
              onChange={setLatestBuild}
              mono
            />
            <Field
              label="Latest version (marketing, display only — e.g. 1.0.1)"
              value={latestVersion}
              onChange={setLatestVersion}
            />
            <Field
              label="Store URL (App Store link the update CTA opens)"
              value={storeUrl}
              onChange={setStoreUrl}
              mono
              placeholder="https://apps.apple.com/app/id000000000"
            />
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-ink-500">
                Update message (shown on the gate screen / banner)
              </span>
              <textarea
                value={message}
                disabled={!canWrite}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:opacity-60"
              />
            </label>

            {!validBounds && (
              <p className="text-xs text-red-600">
                Min build must be ≤ latest build, and both ≥ 0.
              </p>
            )}

            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-ink-400">
                {server ? `Updated ${new Date(server.updated_at).toLocaleString()}` : ''}
              </span>
              <button
                type="button"
                disabled={!canWrite || !dirty || !valid || patchConfig.isPending}
                onClick={() =>
                  patchConfig.mutate({
                    min_supported_build: minNum,
                    latest_build: latestNum,
                    latest_version: latestVersion.trim(),
                    update_message: message,
                    store_url: storeUrl.trim(),
                  })
                }
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {patchConfig.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>

            {!canWrite && (
              <p className="text-xs text-ink-400">
                Read-only — editing needs the <code>plans.write</code> permission.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
